import { Injectable } from '@angular/core';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { BehaviorSubject, Observable } from 'rxjs';
import { Styling } from '../../constants/styling.constants';
import { WindowReferenceService } from '../../services/window-reference/window-reference.service';

/**
 * Service for responsive flags
 *
 * Above and Below are inclusive
 * ex: mdAbove returns true when medium and above
 */
@Injectable({ providedIn: 'root' })
export class ResponsiveService {
    private dataStore: {
        xs: boolean;
        sm: boolean;
        md: boolean;
        lg: boolean;
        xl: boolean;
    };

    private xsSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private smSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private mdSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private lgSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private xlSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);

    public xs: Observable<boolean>;
    public sm: Observable<boolean>;
    public md: Observable<boolean>;
    public lg: Observable<boolean>;
    public xl: Observable<boolean>;

    private smAboveSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private mdAboveSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private lgAboveSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private xlAboveSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);

    public smAbove: Observable<boolean>;
    public mdAbove: Observable<boolean>;
    public lgAbove: Observable<boolean>;
    public xlAbove: Observable<boolean>;

    private smBelowSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private mdBelowSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private lgBelowSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private xlBelowSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);

    public smBelow: Observable<boolean>;
    public mdBelow: Observable<boolean>;
    public lgBelow: Observable<boolean>;
    public xlBelow: Observable<boolean>;

    constructor(private windowReferenceService: WindowReferenceService) {
        SingletonServiceGuard.register('ResponsiveService');
        this.dataStore = {
            xs: false,
            sm: false,
            md: false,
            lg: false,
            xl: false
        };

        this.xs = this.xsSubject.asObservable();
        this.sm = this.smSubject.asObservable();
        this.md = this.mdSubject.asObservable();
        this.lg = this.lgSubject.asObservable();
        this.xl = this.xlSubject.asObservable();

        this.smAbove = this.smAboveSubject.asObservable();
        this.mdAbove = this.mdAboveSubject.asObservable();
        this.lgAbove = this.lgAboveSubject.asObservable();
        this.xlAbove = this.xlAboveSubject.asObservable();

        this.smBelow = this.smBelowSubject.asObservable();
        this.mdBelow = this.mdBelowSubject.asObservable();
        this.lgBelow = this.lgBelowSubject.asObservable();
        this.xlBelow = this.xlBelowSubject.asObservable();

        this.windowReferenceService.resizeStream.subscribe(({ width }) => {
            this.populateValues(width);
        });
    }

    private populateValues(width: number): void {
        this.dataStore.xs = width < Styling.smStart;

        this.dataStore.sm = width >= Styling.smStart && width < Styling.mdStart;

        this.dataStore.md = width >= Styling.mdStart && width < Styling.lgStart;

        this.dataStore.lg = width >= Styling.lgStart && width < Styling.xlStart;

        this.dataStore.xl = width >= Styling.xlStart;

        this.xsSubject.next(this.dataStore.xs);
        this.smSubject.next(this.dataStore.sm);
        this.mdSubject.next(this.dataStore.md);
        this.lgSubject.next(this.dataStore.lg);
        this.xlSubject.next(this.dataStore.xl);

        this.smAboveSubject.next(
            this.dataStore.sm || this.dataStore.md || this.dataStore.lg || this.dataStore.xl
        );
        this.mdAboveSubject.next(this.dataStore.md || this.dataStore.lg || this.dataStore.xl);
        this.lgAboveSubject.next(this.dataStore.lg || this.dataStore.xl);
        this.xlAboveSubject.next(this.dataStore.xl);

        this.smBelowSubject.next(this.dataStore.sm || this.dataStore.xs);
        this.mdBelowSubject.next(this.dataStore.md || this.dataStore.sm || this.dataStore.xs);
        this.lgBelowSubject.next(
            this.dataStore.lg || this.dataStore.md || this.dataStore.sm || this.dataStore.xs
        );
        this.xlBelowSubject.next(
            this.dataStore.xl ||
                this.dataStore.lg ||
                this.dataStore.md ||
                this.dataStore.sm ||
                this.dataStore.xs
        );
    }
}
